import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CASES } from "@/lib/caseStudies";
import { scrollToSection } from "@/lib/scroll";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useEffect } from "react";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const study = CASES.find((c) => c.slug === slug);
  usePageMeta(
    study ? `${study.client} — ${study.category} Case Study | Build Right Partners` : "Case Study | Build Right Partners",
    study?.summary || "Client case study from Build Right Partners."
  );

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [slug]);

  const goContact = () => {
    navigate("/");
    setTimeout(() => scrollToSection("#contact"), 450);
  };

  if (!study) {
    return (
      <main className="bg-[#060B18] text-slate-100 min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 pt-48 pb-32 text-center">
          <p className="font-serif text-3xl italic text-amber-100">Case study not found.</p>
          <Link to="/" className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.25em] text-amber-300 border-b border-amber-400/40 pb-1">
            Back to the site
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid="case-study-page" className="bg-[#060B18] text-slate-100 min-h-screen">
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-10">
        <Link
          data-testid="case-study-back-link"
          to="/"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
            setTimeout(() => scrollToSection("#work"), 450);
          }}
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber-300 transition-colors"
        >
          <ArrowLeft size={14} /> All Work
        </Link>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90"
        >
          {study.category}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400"
        >
          {study.location}
        </motion.p>
        <h1 className="mt-4 font-serif font-bold tracking-tight leading-[1.05] text-4xl sm:text-6xl text-amber-50">
          <span className="block overflow-hidden pb-1">
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="block"
            >
              {study.client}
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-slate-300"
        >
          {study.summary}
        </motion.p>
      </section>

      <motion.section
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-7xl mx-auto px-6 lg:px-10"
      >
        <div className="relative overflow-hidden border border-amber-500/15">
          <img
            src={study.img}
            alt={`${study.client} — ${study.category}`}
            className="w-full aspect-[21/9] object-cover"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060B18]/70 via-transparent to-transparent" />
          <span className="absolute bottom-6 left-6 font-serif italic text-2xl sm:text-3xl text-amber-300">{study.metric}</span>
          <span className="absolute top-4 right-4 h-4 w-4 border-t border-r border-amber-400/60" aria-hidden="true" />
          <span className="absolute bottom-4 left-4 h-4 w-4 border-b border-l border-amber-400/60" aria-hidden="true" />
        </div>
      </motion.section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-testid="case-study-challenge"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-4">01 — The Challenge</p>
          <p className="text-base font-light leading-relaxed text-slate-300">{study.challenge}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="case-study-approach"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-4">02 — The Approach</p>
          <p className="text-base font-light leading-relaxed text-slate-300">{study.approach}</p>
        </motion.div>
      </section>

      <section className="border-y border-amber-500/15 bg-[#0A1128]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-10">03 — The Results</p>
          <div className="grid sm:grid-cols-3 gap-10" data-testid="case-study-results">
            {study.results.map(([value, label], i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <p className="font-serif text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11]">{value}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-center">
        <p className="font-serif italic text-3xl sm:text-4xl text-amber-50">Ready to be the next story?</p>
        <button
          data-testid="case-study-cta"
          onClick={goContact}
          className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#060B18] font-semibold hover:shadow-[0_0_35px_rgba(212,175,55,0.35)] transition-shadow duration-500"
        >
          Start a Project
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
        </button>
      </section>

      <Footer />
    </main>
  );
}
